import { useMemo } from "react";
import { Button, Typography } from "@/components/atoms";
import { DataTable, EmptyState } from "@/components/molecules";
import { DynamicPricingState } from "@/redux/slices/trip/types";
import { generateData, generateColumns } from "./helpers";
import { isEmpty } from "ramda";
import { useNavigate } from "react-router-dom";
import { getCurrencySymbol } from "@/utils";

interface StateRatesPanelProps {
  state: DynamicPricingState;
}

export const StateRatesPanel = ({ state }: StateRatesPanelProps) => {
  const navigate = useNavigate();
  const { name, baseInterval, intervals, currency, distanceUnit } = state;
  const currencySymbol = getCurrencySymbol(currency) ? getCurrencySymbol(currency) : "";

  const data = useMemo(
    () => generateData(baseInterval, intervals, currency, distanceUnit),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intervals]
  );

  const columns = useMemo(
    () => generateColumns(distanceUnit),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex flex-col xl:flex-row justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Typography variant="h2">{name}</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">{`Set up kilometer-based pricing. Add different rates for different distances. For example, ${currencySymbol}5/${distanceUnit} for trips under 10${distanceUnit} and ${currencySymbol}10/${distanceUnit} for longer trips`}</Typography>
        </div>
        {!isEmpty(data) && (
          <div>
            <Button variant="primary" size="sm" startIcon="Edit2" onClick={() => navigate("edit")}>Edit</Button>
          </div>
        )}
      </div>
      {isEmpty(data) ? (
        <div className="rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray">
          <EmptyState title="Nothing here… yet" description="You haven't added anything to this section. Get started by clicking the button below.">
            <Button variant="primary" size="md" startIcon="Add" onClick={() => navigate("edit")}>Add Custom Rates</Button>
          </EmptyState>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={data}
          className="[&_table]:table-auto overflow-x-auto w-full bg-white"
        />
      )}
    </div>
  );
};
